import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// @mui material components
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Project Components
import ProductionCard from '../components/ProductionCard';

// Search Context
import { useSearch } from 'layouts/production/Pages/SearchProvider';

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from 'context';

// Tag API
import { getTags } from 'services/api/general/tag';
import { updateProduction } from 'services/api/production';
import { notify } from 'helpers/notificationHelper';
import { config } from 'Constants';

// Hooks
import useWindowDimensions from 'hooks/useWindowDimensions';

// Üretim güncelleme fonksiyonu
const updateProductionAndNotify = (productionID, updateData) => {
    if (!productionID) {
        console.error('Production ID is null or undefined');
        notify(false, 'Production ID is missing');
        return;
    }

    console.log('Updating production:', productionID, 'with data:', updateData);

    updateProduction(productionID, updateData)
        .then((rp) => {
            if (rp.data && rp.data.success) {
                notify(rp.data.success, rp.data.message || 'Üretim başarıyla güncellendi');
            } else {
                notify(false, 'Beklenmeyen bir yanıt alındı');
            }
        })
        .catch((err) => {
            console.error('Error updating production:', err);
            notify(false, err?.response?.data?.message || 'Üretim güncellenirken bir hata oluştu');
        });
};

// Sürükleme işlemi bittiğinde çalışacak fonksiyon
const onDragEnd = (result, columns, setColumns) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
        // Farklı kolonlar arasında taşıma
        const sourceColumn = columns[source.droppableId];
        const destColumn = columns[destination.droppableId];
        const sourceItems = [...sourceColumn.items];
        const destItems = [...destColumn.items];
        const [removed] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, removed);

        // Değişen üretimin ID'sini al
        const changedItem = destItems[destination.index];
        const productionID = changedItem?.id;

        // Güncelleme verisini hazırla
        const updateData = {};

        // Status değişimini belirle - yeni şema için güncellendi
        if (destination.droppableId === 'empty') {
            updateData.status = null;
        } else {
            // Artık status doğrudan Tag ObjectId olduğu için direkt atıyoruz
            updateData.status = destination.droppableId;
        }

        // Hedef konumda uygun bir order değeri belirle
        updateData.order = calculateNewOrderValue(destItems, destination.index);

        // Taşınan karta yeni order değerini ata
        destItems[destination.index].order = updateData.order;

        // API'yi çağır ve kullanıcıya bildir
        updateProductionAndNotify(productionID, updateData);

        // State'i güncelle - sıralama yapmadan direkt kullan
        // Kullanıcının taşıdığı pozisyon korunmalı
        setColumns({
            ...columns,
            [source.droppableId]: { ...sourceColumn, items: sourceItems },
            [destination.droppableId]: { ...destColumn, items: destItems },
        });
    } else {
        // Aynı kolon içinde taşıma
        const column = columns[source.droppableId];
        const copiedItems = [...column.items];
        const [removed] = copiedItems.splice(source.index, 1);
        copiedItems.splice(destination.index, 0, removed);

        // Değişen üretimin ID'sini al
        const changedItem = copiedItems[destination.index];
        const productionID = changedItem?.id;

        // Yeni bir order değeri hesapla
        const newOrderValue = calculateNewOrderValue(copiedItems, destination.index);

        // Taşınan karta yeni order değerini ata
        copiedItems[destination.index].order = newOrderValue;

        // API'yi çağır
        updateProductionAndNotify(productionID, { order: newOrderValue });

        // State'i güncelle - sıralama yapmadan direkt kullan
        // Çünkü kullanıcı kartı istediği pozisyona taşıdı, o pozisyonda kalmalı
        setColumns({
            ...columns,
            [source.droppableId]: { ...column, items: copiedItems },
        });
    }
};

// Yeni bir order değeri hesaplayan fonksiyon
const calculateNewOrderValue = (items, currentIndex) => {
    const SPACING = 1000000;

    // Eğer liste boşsa
    if (items.length === 0) {
        return SPACING;
    }

    // Eğer tek eleman varsa
    if (items.length === 1) {
        return SPACING;
    }

    // Taşınan kartı çıkar ve kalan kartları order'a göre sırala
    const otherItems = items.filter((_, index) => index !== currentIndex);
    otherItems.sort((a, b) => (a.order || 0) - (b.order || 0));

    // En başa taşınıyorsa
    if (currentIndex === 0) {
        if (otherItems.length === 0) return SPACING;
        const firstOrder = otherItems[0].order || SPACING;
        return Math.max(firstOrder - SPACING, SPACING / 2);
    }

    // En sona taşınıyorsa
    if (currentIndex === items.length - 1) {
        if (otherItems.length === 0) return SPACING;
        const lastOrder = otherItems[otherItems.length - 1].order || 0;
        return lastOrder + SPACING;
    }

    // Ortaya taşınıyorsa
    // currentIndex-1 ve currentIndex pozisyonlarındaki kartların order değerleri arasına yerleştir
    const beforeIndex = currentIndex - 1;
    const afterIndex = currentIndex; // Çünkü taşınan kartı çıkardık

    const beforeOrder = beforeIndex >= 0 ? otherItems[beforeIndex]?.order || 0 : 0;
    const afterOrder =
        afterIndex < otherItems.length
            ? otherItems[afterIndex]?.order || beforeOrder + 2 * SPACING
            : beforeOrder + 2 * SPACING;

    // İki order değeri arasında yeterli boşluk var mı kontrol et
    if (afterOrder - beforeOrder < 2) {
        // Yeterli boşluk yok, tüm kartları yeniden sırala
        console.log('Yeterli boşluk yok, kartlar yeniden sıralanıyor...');
        rebalanceColumnItems(otherItems);

        // Yeniden sıralama sonrası tekrar hesapla
        const newBeforeOrder = beforeIndex >= 0 ? otherItems[beforeIndex]?.order || 0 : 0;
        const newAfterOrder =
            afterIndex < otherItems.length
                ? otherItems[afterIndex]?.order || newBeforeOrder + 2 * SPACING
                : newBeforeOrder + 2 * SPACING;

        return Math.floor((newBeforeOrder + newAfterOrder) / 2);
    }

    // Yeterli boşluk var, arada bir değer döndür
    return Math.floor((beforeOrder + afterOrder) / 2);
};

// Bir kolondaki tüm öğelerin order değerlerini yeniden dengeleyen fonksiyon
const rebalanceColumnItems = (items) => {
    console.log('Kartların order değerleri yeniden dengeleniyor...', items.length, 'kart');

    // Çok daha geniş aralıkla yeniden numaralandır
    const REBALANCE_SPACING = 1000000; // 1 milyon

    // Her karta, pozisyonuna göre yeni bir order değeri ata
    items.forEach((item, index) => {
        const newOrder = (index + 1) * REBALANCE_SPACING;
        item.order = newOrder;

        // Değişiklikleri veritabanına kaydet
        updateProduction(item.id, { order: newOrder }).catch((err) =>
            console.error(`Order rebalancing failed for item ${item.id}:`, err)
        );
    });
};

export default function ProductionsKanban() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { data: all, queryInfo } = useSearch();
    const [columns, setColumns] = useState({});
    const [statusTags, setStatusTags] = useState([]);
    const [isLoadingTags, setIsLoadingTags] = useState(true);
    const [maxColumnHeight, setMaxColumnHeight] = useState(0);
    const user = JSON.parse(localStorage.getItem('user'));
    const { height, width } = useWindowDimensions();

    // Fetch status tags from API
    useEffect(() => {
        const fetchStatusTags = async () => {
            try {
                setIsLoadingTags(true);
                const { data } = await getTags(config.fixedID.PRODUCTION_STATUS_GROUP_ID);
                if (data && data.result && data.result.tags) {
                    setStatusTags(data.result.tags);
                }
            } catch (error) {
                console.error('Error fetching status tags:', error);
            } finally {
                setIsLoadingTags(false);
            }
        };

        fetchStatusTags();
    }, []);

    // Helper function to get status ID from production
    const getProductionStatusId = (production) => {
        if (!production || !production.status) return 'empty';

        // Artık status doğrudan bir Tag referansı olduğu için
        if (production.status && typeof production.status === 'object' && production.status._id) {
            return production.status._id;
        } else if (production.status && typeof production.status === 'string') {
            return production.status; // Doğrudan ID olarak zaten
        }

        return 'empty';
    };

    // Create columns when both productions and status tags are available
    useEffect(() => {
        if (all?.result && !isLoadingTags) {
            const productions = all.result;

            // Önce tag'leri sırala (order varsa ona göre)
            let sortedTags = [...statusTags];
            sortedTags.sort((a, b) => (a.order || 0) - (b.order || 0));

            // Kolonları oluştur
            const columnsData = {};

            // Belirtilmemiş kategorisi
            columnsData.empty = {
                name: 'Belirtilmemiş',
                items: [],
            };

            // Her tag için bir kolon oluştur
            sortedTags.forEach((tag) => {
                columnsData[tag._id] = {
                    name: tag.name,
                    color: tag.color,
                    items: [],
                    tagDetail: tag,
                };
            });

            // Üretimleri uygun kolonlara dağıt
            productions.forEach((production) => {
                const statusId = getProductionStatusId(production);

                if (columnsData[statusId]) {
                    columnsData[statusId].items.push({
                        id: production._id,
                        name: production.productionName,
                        content: production,
                        order: production.order || 0,
                    });
                } else {
                    columnsData.empty.items.push({
                        id: production._id,
                        name: production.productionName,
                        content: production,
                        order: production.order || 0,
                    });
                }
            });

            // Her kolonun içindeki öğeleri order alanına göre sırala
            Object.keys(columnsData).forEach((key) => {
                columnsData[key].items.sort((a, b) => (a.order || 0) - (b.order || 0));
            });

            // En uzun kolonu bul
            const columnCounts = Object.values(columnsData).map((col) => col.items.length);
            const maxItemCount = Math.max(...columnCounts);
            setMaxColumnHeight(maxItemCount);

            setColumns(columnsData);
        }
    }, [all?.result, statusTags, isLoadingTags]);

    if (queryInfo.isError) {
        return <div>{JSON.stringify(queryInfo)}</div>;
    }
    if (queryInfo.isLoading || isLoadingTags) {
        return <div>loading search results...</div>;
    }

    if (Object.keys(columns).length === 0) {
        return (
            <Card>
                <MDBox p={3} textAlign="center">
                    <MDTypography>
                        Kanban görünümü için üretim verileri yüklenemedi veya uygun durum bilgisi bulunamadı.
                    </MDTypography>
                </MDBox>
            </Card>
        );
    }

    return (
        <Card>
            <MDBox py={3}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    p={0}
                    m={0}
                    sx={{
                        width: '100%',
                    }}
                >
                    <DragDropContext onDragEnd={(result) => onDragEnd(result, columns, setColumns)}>
                        {Object.entries(columns).map(([id, column]) => (
                            <KanbanColumn
                                key={id}
                                id={id}
                                column={column}
                                height={height}
                                darkMode={darkMode}
                                statusTags={statusTags}
                                user={user}
                                totalColumns={Object.keys(columns).length}
                                maxColumnHeight={maxColumnHeight}
                            />
                        ))}
                    </DragDropContext>
                </MDBox>
            </MDBox>
        </Card>
    );
}

// Kanban kolonunu render eden bileşen
const KanbanColumn = ({ id, column, height, darkMode, statusTags, user, totalColumns, maxColumnHeight }) => (
    <MDBox
        sx={[
            {
                borderRadius: '10px',
                flexGrow: 1,
                flexBasis: `${100 / totalColumns}%`,
                minWidth: 'unset',
                maxWidth: 'unset',
                height: 'fit-content',
                boxShadow: '0 2px 4px 0 rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            },
            darkMode
                ? {
                      background: 'rgba(255,255,255,0.05)',
                  }
                : {
                      background: '#f5f5f6',
                  },
        ]}
        mx={0.5}
        p={1}
    >
        <MDTypography
            variant="h6"
            fontWeight="medium"
            textAlign="center"
            mb={1}
            color={column.color ? undefined : 'text'}
            sx={{
                color: column.color ? column.color : undefined,
                fontSize: '0.9rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {column.name}
        </MDTypography>

        <Droppable droppableId={id}>
            {(provided) => (
                <MDBox
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={[
                        {
                            paddingBottom: '16px',
                            transition: 'background-color 0.2s ease',
                            backgroundColor: provided.isDraggingOver
                                ? darkMode
                                    ? 'rgba(255,255,255,0.1)'
                                    : 'rgba(0,0,0,0.05)'
                                : 'transparent',
                        },
                        maxColumnHeight > 0
                            ? {
                                  minHeight: `${maxColumnHeight * 125}px`,
                              }
                            : {
                                  minHeight: '50px',
                              },
                    ]}
                >
                    {column.items.map((item, index) => (
                        <ProductionItem
                            key={item.id}
                            item={item}
                            index={index}
                            darkMode={darkMode}
                            statusTags={statusTags}
                            user={user}
                        />
                    ))}
                    {provided.placeholder}
                </MDBox>
            )}
        </Droppable>
    </MDBox>
);

// Sürüklenebilir üretim kartını render eden bileşen
const ProductionItem = ({ item, index, darkMode, statusTags, user }) => {
    // Border style based on OnayDurum
    const getBorderStyle = () => {
        const durum = item.content?.aaroFields?.OnayDurum;
        if (durum === 0) {
            return {
                borderLeft: '3px solid #2196f3',
            };
        }
        if (durum === -1) {
            return {
                borderLeft: '3px solid #f44336',
            };
        }
        return {};
    };
    return (
        <Draggable draggableId={item.id} index={index}>
            {(provided, snapshot) => (
                <MDBox
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{
                        padding: 1,
                        borderRadius: '8px',
                        marginBottom: 1,
                        backgroundColor: snapshot.isDragging
                            ? darkMode
                                ? 'rgba(25, 25, 25, 0.9)'
                                : '#fff'
                            : darkMode
                            ? 'rgba(20, 20, 20, 0.8)'
                            : '#fff',
                        boxShadow: snapshot.isDragging
                            ? '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)'
                            : '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                        transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                        ...getBorderStyle(),
                        ...provided.draggableProps.style,
                    }}
                >
                    <ProductionCard
                        productionName={item.content.productionName}
                        status={item.content.status}
                        createdAt={item.content.createdAt}
                        aaroCustomerName={item.content.aaroCustomerName}
                        responsibles={item.content.responsibles || []}
                        isPinned={item.content.pinnedBy?.includes(user?.userID)}
                        productionID={item.content.aaroOrder}
                        statusTags={statusTags}
                        taskList={item.content.taskList}
                        completionPercentage={item.content.completionPercentage}
                        taskCount={item.content.taskCount}
                        tags={item.content.tags}
                        aaroFields={item.content.aaroFields}
                    />
                </MDBox>
            )}
        </Draggable>
    );
};
